import { LAYER } from '../components/layers';
import { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { isNavNodeVisible, NAV_GROUP_LABEL, type NavNode } from './sidebarConfig';

const PANEL_WIDTH = 224;
const GAP = 8;
const VIEWPORT_MARGIN = 8;

/**
 * The flyout a collapsed rail item opens — the only way to reach a group's children once
 * the labels are gone.
 *
 * Positioned `fixed` and portalled to the body for the same reason `<Menu>` is: the rail
 * scrolls, and a panel clipped by its own container would be unreachable. Position is
 * written directly in the ref callback rather than held in state — the panel has to be
 * measured before it can be clamped to the viewport, and routing that through state costs
 * a render and a visible jump.
 */
export function SidebarFlyout({
  node,
  triggerRef,
  id,
  onRequestClose,
  onPointerEnter,
  onPointerLeave,
  autoFocus = false,
}: {
  node: NavNode;
  triggerRef: React.RefObject<HTMLElement | null>;
  id: string;
  onRequestClose: (options?: { restoreFocus?: boolean }) => void;
  onPointerEnter: () => void;
  onPointerLeave: () => void;
  /** Set when the flyout was opened from the keyboard: focus moves into the panel. */
  autoFocus?: boolean;
}) {
  const panelRef = useRef<HTMLDivElement | null>(null);

  const place = useCallback(
    (element: HTMLDivElement | null) => {
      panelRef.current = element;

      const trigger = triggerRef.current;
      if (!element || !trigger) return;

      const bounds = trigger.getBoundingClientRect();
      const height = element.offsetHeight;

      // Prefer aligning with the trigger; slide up only as far as the viewport demands.
      const maxTop = Math.max(VIEWPORT_MARGIN, window.innerHeight - height - VIEWPORT_MARGIN);

      /*
        Anchored to the RAIL's right edge, not the trigger's. The trigger is a 40px button
        inside a 64px rail, so measuring from the button puts the panel back on top of the
        rail's own border.
      */
      const rail = trigger.closest('aside');
      const leftEdge = (rail ?? trigger).getBoundingClientRect().right;

      element.style.top = `${Math.min(Math.max(VIEWPORT_MARGIN, bounds.top), maxTop)}px`;
      element.style.left = `${leftEdge + GAP}px`;
    },
    [triggerRef],
  );

  useEffect(() => {
    if (!autoFocus) return;

    // Fall back to the panel itself: a group of sample entries has no focusable row, and
    // leaving focus behind on the trigger would strand Escape.
    const first = panelRef.current?.querySelector<HTMLElement>('[role="menuitem"]');
    (first ?? panelRef.current)?.focus();
  }, [autoFocus]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onRequestClose({ restoreFocus: true });
      }
    }

    function onPointerDown(event: MouseEvent) {
      const target = event.target as Node;

      if (!panelRef.current?.contains(target) && !triggerRef.current?.contains(target)) {
        onRequestClose();
      }
    }

    // The rail scrolls under the panel, so closing beats chasing the trigger.
    function onScroll(event: Event) {
      if (!panelRef.current?.contains(event.target as Node)) {
        onRequestClose();
      }
    }

    // Named, not inline: an anonymous handler cannot be removed, and this effect re-runs.
    function onResize() {
      onRequestClose();
    }

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [onRequestClose, triggerRef]);

  return createPortal(
    <div
      ref={place}
      id={id}
      role="menu"
      aria-label={node.label}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      tabIndex={-1}
      style={{ position: 'fixed', width: PANEL_WIDTH }}
      className={`modern-scrollbar ${LAYER.PANEL} max-h-[80vh] overflow-y-auto rounded-panel border border-slate-200 bg-white py-1.5 shadow-raised`}
    >
      {/*
        The rail hid this label; the flyout is where it comes back — and it comes back as the
        SAME kind of heading the expanded rail uses over a group, not as a page title over a
        menu. The two are one navigation at two widths.
      */}
      <p className={`px-3 pb-1.5 ${NAV_GROUP_LABEL}`}>{node.label}</p>

      <FlyoutNodes nodes={node.children ?? []} depth={0} idPrefix={id} onNavigate={onRequestClose} />
    </div>,
    document.body,
  );
}

/**
 * The subtree, flattened into labelled groups.
 *
 * A child that has children of its own becomes a **group label** with its rows beneath it,
 * rather than another level of expand/collapse — a flyout the user is holding a pointer
 * over is the wrong place to ask them to click twice.
 */
function FlyoutNodes({
  nodes,
  depth,
  idPrefix,
  onNavigate,
}: {
  nodes: NavNode[];
  depth: number;
  idPrefix: string;
  onNavigate: () => void;
}) {
  const { hasPermission } = useAuth();
  const visible = nodes.filter((node) => isNavNodeVisible(node, hasPermission));

  if (visible.length === 0) {
    return null;
  }

  return (
    <ul className="flex flex-col">
      {visible.map((node) =>
        node.children ? (
          <li key={node.key} className="mt-1 first:mt-0">
            <p
              id={`${idPrefix}-group-${node.key}`}
              style={{ paddingLeft: 12 + depth * 10 }}
              className={`py-1 pr-3 ${NAV_GROUP_LABEL}`}
            >
              {node.label}
            </p>

            <FlyoutNodes
              nodes={node.children}
              depth={depth + 1}
              idPrefix={idPrefix}
              onNavigate={onNavigate}
            />
          </li>
        ) : (
          <li key={node.key}>
            <FlyoutLeaf node={node} depth={depth} idPrefix={idPrefix} onNavigate={onNavigate} />
          </li>
        ),
      )}
    </ul>
  );
}

function FlyoutLeaf({
  node,
  depth,
  idPrefix,
  onNavigate,
}: {
  node: NavNode;
  depth: number;
  idPrefix: string;
  onNavigate: () => void;
}) {
  const padding = 12 + depth * 10;

  // A sample entry renders in the flyout but goes nowhere, exactly as it does in the rail.
  if (node.sample || !node.to) {
    return (
      <span
        id={`${idPrefix}-${node.key}`}
        title="Sample entry — not wired to a route."
        style={{ paddingLeft: padding }}
        className="flex cursor-default items-center gap-2.5 py-1.5 pr-3 text-sm text-slate-400"
      >
        {node.icon ? <node.icon className="h-4 w-4 shrink-0" aria-hidden="true" /> : null}
        <span className="min-w-0 truncate">{node.label}</span>
      </span>
    );
  }

  return (
    <NavLink
      to={node.to}
      id={`${idPrefix}-${node.key}`}
      role="menuitem"
      onClick={onNavigate}
      style={{ paddingLeft: padding }}
      className={({ isActive }) =>
        [
          'relative flex items-center gap-2.5 py-1.5 pr-3 text-sm transition',
          isActive
            ? 'text-slate-900'
            : // Same step as the expanded rail — the two draw one nav and must not disagree.
              node.secondary
              ? 'text-slate-500 hover:bg-slate-100 hover:text-slate-800'
              : 'text-slate-600 hover:bg-slate-100 hover:text-slate-800',
        ].join(' ')
      }
    >
      {({ isActive }) => (
        <>
          {isActive ? <span aria-hidden="true" className="absolute left-0 top-1/2 h-4 w-1 -translate-y-1/2 rounded-r bg-brand-500" /> : null}
          {node.icon ? <node.icon className="h-4 w-4 shrink-0" aria-hidden="true" /> : null}
          <span className="min-w-0 truncate">{node.label}</span>
        </>
      )}
    </NavLink>
  );
}
